import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { BankLine } from '@modules-deprecated/bankReconciliation/types'
import { cleanId } from '@modules-deprecated/bankReconciliation/utils'

import { QueryKeys } from '../../../enums/queryKeys'
import { getBankLineMatchSuggestions } from '../query-api'
import { BookkeepingItem } from '../types/bookkeepingItem'
import { isCashBasedNonExactSuggestion } from '../utils'

interface UseBankLineSuggestionProps {
  accountId?: string
  bankLine?: BankLine
  bookkeepingItems?: BookkeepingItem[]
  enabled?: boolean
  organizationId?: string
}

export const useBankLineSuggestion = ({
  accountId,
  bankLine,
  bookkeepingItems,
  enabled: enabledProp = true,
  organizationId,
}: UseBankLineSuggestionProps) => {
  const enabled =
    enabledProp && !!bankLine?.id && !!organizationId && !!accountId && bankLine.match?.isApproved !== true

  const { data, isLoading } = useQuery(
    [QueryKeys.BankLineSuggestions, bankLine?.id],
    () =>
      getBankLineMatchSuggestions({
        organizationId: organizationId as string,
        accountId: accountId as string,
        bankLineIds: [bankLine?.id as string],
      }),
    {
      enabled,
    },
  )

  const suggestion = useMemo(() => {
    if (!data?.suggestions) {
      return undefined
    }

    const suggestionsArray = Object.values(data.suggestions)
    const rawSuggestion = suggestionsArray[0]

    if (!rawSuggestion) {
      return undefined
    }

    if (bookkeepingItems) {
      const reconcilable = rawSuggestion.reconcilables[0]
      if (reconcilable) {
        const [, extractedId] = cleanId(reconcilable.id)
        const bookkeepingItem = bookkeepingItems.find((item) => item.id === extractedId)
        if (isCashBasedNonExactSuggestion(rawSuggestion, bookkeepingItem?.accountingBasis)) {
          return undefined
        }
      }
    }

    return rawSuggestion
  }, [data?.suggestions, bookkeepingItems])

  return { suggestion, isLoading }
}
